import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import CockpitHero from "../../components/CockpitHero";
import AIChatPanel from "../../components/AIChatPanel";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Sparkles,
  Globe2,
  Gavel,
  ArrowUpRight,
  ScrollText,
  Vote,
} from "lucide-react";

const STARTERS = [
  "Summarise my country's foreign policy on this agenda in 4 bullets",
  "Help me draft a 90-second opening speech",
  "What blocs is my country likely to align with?",
  "List 5 research checklist items I must complete before day one",
  "Suggest 3 moderated caucus topics that favour my country",
];

export default function DelegatePrepAssistantPage() {
  const [registrations, setRegistrations] = useState([]);
  const [country, setCountry] = useState("");
  const [committee, setCommittee] = useState("");
  const [agenda, setAgenda] = useState("");
  const [eventId, setEventId] = useState("");

  useEffect(() => {
    // Load delegate's allocations to auto-fill country & committee
    api.get("/registrations").then((r) => {
      setRegistrations(r.data || []);
      const reg = r.data?.[0];
      if (reg) {
        setCountry(reg.country || "");
        setCommittee(reg.council_name || reg.committee_name || "");
        setEventId(reg.event_id);
        if (reg.event_id) {
          api.get(`/events/${reg.event_id}`).then((er) => {
            const ev = er.data?.event || er.data;
            const council = (er.data?.councils || []).find((c) => c.id === reg.council_id);
            if (council) {
              setCommittee(council.name);
              setAgenda(council.agenda || council.topic || "");
            } else if (ev?.theme) {
              setAgenda(ev.theme);
            }
          }).catch(() => {});
        }
      }
    }).catch(() => {});
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        <CockpitHero
          testId="ai-prep-hero"
          eyebrow="AI Delegate Prep · Intelligence briefing"
          eyebrowIcon={Sparkles}
          title="Your private"
          titleAccent="prep coach"
          titleTail="."
          description="Country-aware, agenda-aware guidance tailored to your committee. Save useful answers into the Position Paper Builder with one click."
          meta={[
            ...(country ? [["DESK", country]] : []),
            ...(committee ? [["COMMITTEE", committee]] : []),
            ...(agenda ? [["AGENDA", agenda.slice(0, 60)]] : []),
          ]}
        />

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          {/* Left: context inputs + quick actions */}
          <div className="lg:col-span-4 space-y-5">
            <div className="rounded-sm bg-white border border-slate-200 p-5">
              <p className="eyebrow text-brand-gold-dark">
                Briefing context
              </p>
              <div className="mt-4 space-y-3" data-testid="prep-context-form">
                <div>
                  <Label className="text-xs">Country</Label>
                  <Input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. India" data-testid="prep-country" />
                </div>
                <div>
                  <Label className="text-xs">Committee</Label>
                  <Input value={committee} onChange={(e) => setCommittee(e.target.value)} placeholder="e.g. UNSC" data-testid="prep-committee" />
                </div>
                <div>
                  <Label className="text-xs">Agenda / topic</Label>
                  <Input value={agenda} onChange={(e) => setAgenda(e.target.value)} placeholder="e.g. South China Sea" data-testid="prep-agenda" />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <Stat icon={Globe2} label="Country" value={country || "—"} />
                <Stat icon={Gavel} label="Committee" value={committee || "—"} />
                <Stat icon={Vote} label="Events" value={registrations.length} />
              </div>
            </div>

            <div className="rounded-2xl bg-[#0A192F] text-white p-5 border border-[#D4AF37]/20">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                Connect to builders
              </p>
              <p className="mt-2 text-sm text-slate-300">
                Take any AI insight straight into your structured workflow.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-2">
                <Link to="/dashboard/papers/new" className="flex items-center justify-between rounded-md bg-white/5 hover:bg-white/10 px-3 py-2.5 text-sm">
                  <span className="flex items-center gap-2"><ScrollText className="h-4 w-4 text-[#D4AF37]" /> Position Paper Builder</span>
                  <ArrowUpRight className="h-4 w-4 text-slate-400" />
                </Link>
                <Link to="/dashboard/resolutions/new" className="flex items-center justify-between rounded-md bg-white/5 hover:bg-white/10 px-3 py-2.5 text-sm">
                  <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#D4AF37]" /> Resolution Builder</span>
                  <ArrowUpRight className="h-4 w-4 text-slate-400" />
                </Link>
              </div>
            </div>
          </div>

          {/* Right: chat panel */}
          <div className="lg:col-span-8">
            <AIChatPanel
              title="Delegate Prep Assistant"
              subtitle={country && committee ? `${country} · ${committee}` : "Set your country & committee on the left"}
              endpoint="/ai/delegate-prep"
              fixedPayload={{ country, committee, agenda }}
              starters={STARTERS}
              api={api}
              testidPrefix="prep-chat"
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-100 px-2 py-2.5">
      <Icon className="h-4 w-4 text-[#0F4C81] mx-auto" />
      <p className="mt-1.5 text-xs font-semibold text-[#0A192F] truncate">{value}</p>
      <p className="text-[9px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}
